import type { VoiceEventHandler, VoiceEventMap, VoiceEventName } from './types';

/**
 * Minimal typed event emitter shared by the provider implementations.
 *
 * Each implementation translates its SDK's events into these, so the emitter is
 * the one place that owns fan-out and unsubscription. Handler errors are
 * contained: one bad subscriber must not stop the others from being told that a
 * call ended.
 */
export class VoiceEventEmitter {
  // Stored loosely and narrowed on the way in and out. A per-event mapped type
  // cannot be assigned through a generic method — TypeScript has no way to know
  // that `E` on the way in is the same `E` the bucket was created for — so the
  // cast lives here once instead of at every call site.
  private handlers = new Map<VoiceEventName, Set<(payload: never) => void>>();

  on<E extends VoiceEventName>(event: E, handler: VoiceEventHandler<E>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }

    const stored = handler as unknown as (payload: never) => void;
    set.add(stored);
    return () => {
      set?.delete(stored);
    };
  }

  emit<E extends VoiceEventName>(event: E, payload: VoiceEventMap[E]): void {
    const set = this.handlers.get(event) as Set<VoiceEventHandler<E>> | undefined;
    if (!set) return;

    // Copy before iterating: a handler may unsubscribe itself, and mutating the
    // live set mid-iteration would skip its neighbour.
    for (const handler of [...set]) {
      try {
        handler(payload);
      } catch (err) {
        console.error(`voice: ${event} handler threw`, err);
      }
    }
  }

  /** Drop every subscriber. Called on teardown so a destroyed session is inert. */
  clear(): void {
    this.handlers.clear();
  }
}
